import { vars } from '@/shared/ui/theme';
import { style, keyframes } from '@vanilla-extract/css';
import { reducedMotion } from '@/shared/ui/theme';

const P = vars.bright.primary;
const S = vars.bright.secondary;
const WHITE = vars.opacityScale.whiteOverlay;
const BLACK = vars.opacityScale.blackOverlay;

// 옥타곤 chamfer — 모서리는 고정 px, 직선 변만 calc(100% - n)로 늘어난다.
// authContentContainer(28px)와 동일한 size-agnostic 관용구. 모서리는 어떤
// width/height 에서도 절대 스케일되지 않으므로 45° chamfer 와 원형 orb 가 유지된다.
const OUTER_CHAMFER = 48;
const INNER_CHAMFER = 44;
const BEVEL_INSET = 2; // 바깥 링 두께 바로 안쪽에서 시작
const BEVEL_WIDTH = 12; // 바깥 링(2px)과 안쪽 링(14px) 사이

const octagonClip = (c: number) =>
  `polygon(${c}px 0, calc(100% - ${c}px) 0, 100% ${c}px, 100% calc(100% - ${c}px), calc(100% - ${c}px) 100%, ${c}px 100%, 0 calc(100% - ${c}px), 0 ${c}px)`;

// ============================================
// LIGHT — 광원은 위쪽 가운데 하나.
// 바깥 링: 위 가운데가 희게 달아오르고(hot spot) 아래로 갈수록 깊은 파랑으로 가라앉는다.
// 안쪽 링: 반대로 위가 어둡고 아래가 밝다 — 튀어나온 테두리의 안쪽 벽은 위가 그늘에 들고
// 아래가 빛을 받으므로, 두 링의 밝기가 엇갈려야 패널이 한 단 들어가 보인다.
// ============================================
const RING_OUTER_LIGHT = `radial-gradient(40% 56px at 50% 0, ${WHITE.dense}, ${WHITE.transparent}), linear-gradient(180deg, ${P.light} 0%, ${P.default} 30%, ${P.dark} 65%, ${vars.surface.highlight.strong} 100%)`;
const RING_INNER_LIGHT = `linear-gradient(0deg, ${P.light} 0%, ${P.default} 45%, ${P.dark} 100%)`;

// 베벨 띠: 위는 빛이 스치고, 아래로 갈수록 그늘이 짙어진다.
const BEVEL_SHADE = `linear-gradient(180deg, ${WHITE.notable} 0%, ${S.light} 20%, ${WHITE.transparent} 45%, ${BLACK.intense} 100%)`;

// 글로우: 희게 탄 심지 가장자리 → 파란 번짐 → 넓은 시안 헤일로. 링 밝기를 그대로 따라가므로
// 위는 크게, 아래는 작게 번진다.
const RING_GLOW = `drop-shadow(0 0 1px ${P.light}) drop-shadow(0 0 8px ${P.glow}) drop-shadow(0 0 22px ${S.strong})`;

// 링 wrapper 가 글로우를 품으므로 와이프가 끝나도 바깥 번짐이 잘리지 않게 여유를 둔다.
const GLOW_ROOM = 40;

// ============================================
// DRAW-ON REVEAL (clip wipe — 기존 strokeDashoffset 트레이스 대체)
// 위→아래 inset 와이프로 링을 0%→100% 드러낸다. reduced-motion 에서는
// 애니메이션 없이 완전히 그려진 상태로 즉시 표시 (기존 fallback 동일).
//
// 중요: 와이프는 ring 의 clipPath(옥타곤)와 충돌하면 안 되므로 별도 RAPPER 의
// clipPath 에만 적용한다. ring 자신의 clipPath 는 옥타곤 전용 — 와이프는 wrapper
// 가 옥타곤 ring 을 위→아래로 점진 노출하고, 종료(forwards) 시 inset(-GLOW_ROOM) =
// 글로우 여유까지 전부 보인다. clipPath 단일값 충돌 없음.
// ============================================
const frameReveal = keyframes({
  from: { clipPath: `inset(-${GLOW_ROOM}px -${GLOW_ROOM}px 100% -${GLOW_ROOM}px)` },
  to: { clipPath: `inset(-${GLOW_ROOM}px)` },
});

// 와이프 WRAPPER — inset:0 으로 ring 박스를 덮고 clipPath 와이프 + 글로우를 담당.
// ring 의 옥타곤 clipPath 는 건드리지 않는다 (별도 자식 요소).
const revealWrapperBase = style({
  position: 'absolute',
  inset: '0',
  pointerEvents: 'none',
});

export const revealWrapperOuter = style([
  revealWrapperBase,
  {
    filter: RING_GLOW,
    // draw-on: outer 2s / delay .3s (기존 stagger 유지)
    animation: `${frameReveal} ${vars.animation.duration.xslow} ${vars.animation.easing.easeOut} ${vars.animation.duration.normal} forwards`,
    '@media': {
      [reducedMotion]: { animation: 'none', clipPath: 'none' },
    },
  },
]);

export const revealWrapperInner = style([
  revealWrapperBase,
  {
    // draw-on: inner 1.5s / delay .6s (기존 stagger 유지)
    animation: `${frameReveal} ${vars.animation.duration.reveal} ${vars.animation.easing.easeOut} ${vars.animation.duration.sequenceTrail} forwards`,
    '@media': {
      [reducedMotion]: { animation: 'none', clipPath: 'none' },
    },
  },
]);

// ============================================
// ROOT — systemFrameGlitch 가 이 단일 wrapper 에 붙는다 (ring + garnish 한 덩어리로 글리치)
// ============================================
export const systemFrameRoot = style({
  position: 'absolute',
  inset: '0',
  pointerEvents: 'none',
});

// ============================================
// FRAME RING (replaces frameOuter + frameInner + frameGrad + frameGlow)
// mask-composite gradient-border + 고정 px chamfer octagon
// ============================================
const ringBase = style({
  position: 'absolute',
  pointerEvents: 'none',
  WebkitMask: `linear-gradient(${BLACK.solid} 0 0) content-box, linear-gradient(${BLACK.solid} 0 0)`,
  WebkitMaskComposite: 'xor',
  maskComposite: 'exclude', // 내부를 도려내 chamfer 된 RING 만 보이게
});

export const frameRing = style([
  ringBase,
  {
    inset: '0',
    padding: '2px', // = strokeWidth 2 (ring 두께)
    background: RING_OUTER_LIGHT,
    clipPath: octagonClip(OUTER_CHAMFER), // 옥타곤 전용 — 와이프와 충돌 안 함
  },
]);

export const frameRingInner = style([
  ringBase,
  {
    inset: '14px',
    padding: '1px', // = strokeWidth 1
    opacity: 0.6,
    background: RING_INNER_LIGHT,
    clipPath: octagonClip(INNER_CHAMFER), // 옥타곤 전용 — 와이프와 충돌 안 함
  },
]);

// ============================================
// BEVEL — 두 링 사이 띠. 위는 빛이 스치고 아래는 그늘에 들어 테두리가 두께를 가진다.
// ============================================
export const frameBevel = style([
  ringBase,
  {
    inset: `${BEVEL_INSET}px`,
    padding: `${BEVEL_WIDTH}px`,
    background: BEVEL_SHADE,
    clipPath: octagonClip(OUTER_CHAMFER - BEVEL_INSET),
  },
]);

// ============================================
// SHEEN — 반사광 한 줄기가 링을 가로질러 한 번 흐르고 한동안 쉰다.
// 링과 같은 마스크라 링 위에서만 보인다. reduced-motion 에서는 숨긴다.
// ============================================
const sheenSweep = keyframes({
  '0%': { backgroundPosition: '130% 0' },
  '30%, 100%': { backgroundPosition: '-30% 0' },
});

export const frameSheen = style([
  ringBase,
  {
    inset: '0',
    padding: '2px',
    background: `linear-gradient(100deg, ${WHITE.transparent} 40%, ${WHITE.dense} 50%, ${WHITE.transparent} 60%)`,
    backgroundSize: '250% 100%',
    backgroundRepeat: 'no-repeat',
    clipPath: octagonClip(OUTER_CHAMFER),
    animation: `${sheenSweep} calc(${vars.animation.duration.crawl} * 2) ${vars.animation.easing.easeInOut} ${vars.animation.duration.xslow} infinite`,
    '@media': {
      [reducedMotion]: { animation: 'none', display: 'none' },
    },
  },
]);

// ============================================
// REFLECTION — 패널 아래 바닥에 비친 빛. 패널이 바닥 위에 떠 있는 거리를 만든다.
// ============================================
export const frameReflection = style({
  position: 'absolute',
  left: '14%',
  right: '14%',
  top: 'calc(100% - 4px)', // 패널 바닥 바로 아래에서 시작해야 패널에 가리지 않는다
  height: '64px',
  background: `radial-gradient(ellipse at 50% 0%, ${P.glow} 0%, ${S.light} 45%, ${WHITE.transparent} 72%)`,
  filter: 'blur(16px)',
  opacity: 0.9,
  pointerEvents: 'none',
});

// ============================================
// CORNER GARNISH — 고정 76×76 SVG sprite (L-bracket + orb + tick)
// 1:1 viewBox 라 chamfer 대각선 45°, orb 가 항상 진짜 원으로 유지된다.
// 위 두 코너만 빛을 받아 글로우를 두르고, 아래 두 코너는 그늘에 들어 번지지 않는다.
// ============================================
const cornerBase = style({
  position: 'absolute',
  width: 76,
  height: 76,
});

const cornerLit = style({
  filter: `drop-shadow(0 0 3px ${P.default}) drop-shadow(0 0 12px ${S.glow})`,
});

const cornerShade = style({
  opacity: 0.55,
});

export const cornerTL = style([cornerBase, cornerLit, { top: 0, left: 0 }]);
export const cornerTR = style([
  cornerBase,
  cornerLit,
  { top: 0, right: 0, transform: 'rotate(90deg)' },
]);
export const cornerBR = style([
  cornerBase,
  cornerShade,
  { bottom: 0, right: 0, transform: 'rotate(180deg)' },
]);
export const cornerBL = style([
  cornerBase,
  cornerShade,
  { bottom: 0, left: 0, transform: 'rotate(270deg)' },
]);

// ============================================
// SIDE RUNES — 고정 ~16×280 SVG, 세로변 상단 앵커 (panel 이 커져도 늘어나지 않음)
// 내부 Y geometry 는 기존 138→415 band 를 0 기준으로 평행이동해 보존.
// r=3 원은 고정 박스라 원형 유지. 광원에서 멀어지는 아래쪽으로 옅어진다.
// ============================================
const runeBase = style({
  position: 'absolute',
  top: 138,
  width: 16,
  height: 280,
  maskImage: `linear-gradient(180deg, ${BLACK.solid} 0%, ${BLACK.firm} 100%)`,
  WebkitMaskImage: `linear-gradient(180deg, ${BLACK.solid} 0%, ${BLACK.firm} 100%)`,
});

export const runeLeft = style([runeBase, { left: 0 }]);
export const runeRight = style([runeBase, { right: 0 }]);

// ============================================
// SCAN BARS — 고정 ~250×16 SVG, 가로 중앙 정렬, top/bottom 8px (수평 skew 없음)
// 위 스캔바는 광원 바로 아래라 가장 밝고, 아래 스캔바는 가라앉는다.
// ============================================
const scanBase = style({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  width: 250,
  height: 16,
});

export const scanTop = style([
  scanBase,
  { top: 0, filter: `drop-shadow(0 0 4px ${P.default}) drop-shadow(0 0 10px ${S.glow})` },
]);
export const scanBottom = style([scanBase, { bottom: 0, opacity: 0.45 }]);
